import { StyleSheet } from "react-native";

import {
  fonts,
  palette,
  radii,
  shadows,
  spacing,
  type ThemeColors,
} from "~/styles/theme";

export const sidebarInset = spacing.m;

export const makeStylesheet = (colors: ThemeColors) =>
  StyleSheet.create({
    appShell: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.background,
    },
    appShellContent: { flex: 1, minWidth: 0 },
    upgradeBanner: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.l,
      paddingVertical: spacing.s,
      backgroundColor: palette.brandMarigold,
    },
    sidebar: {
      width: 252,
      padding: spacing.l,
      backgroundColor: colors.surfaceNav,
      borderRightWidth: 1,
      borderRightColor: colors.borderStrong,
      ...shadows.sm,
    },
    sidebarBody: { flex: 1, gap: spacing.xs },
    sidebarBrand: {
      marginBottom: spacing.xl,
      paddingHorizontal: spacing.xs,
    },
    syncPill: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
      marginBottom: spacing.l,
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.control,
      backgroundColor: colors.elevated,
      ...shadows.sm,
    },
    syncDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.primary,
    },
    syncDotPending: { backgroundColor: palette.brandMarigold },
    syncDotError: { backgroundColor: colors.danger },
    syncCopy: { flex: 1, minWidth: 0 },
    syncLabel: {
      color: colors.heading,
      fontFamily: fonts.medium,
      fontSize: 12,
    },
    syncHint: {
      color: colors.mutedForeground,
      fontFamily: fonts.regular,
      fontSize: 11,
    },
    navSectionLabel: {
      marginTop: spacing.s,
      marginBottom: spacing.xs,
      paddingHorizontal: sidebarInset,
      color: colors.mutedForeground,
      fontFamily: fonts.medium,
      fontSize: 11,
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    sidebarDivider: {
      marginVertical: spacing.m,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    navItem: {
      minHeight: 42,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.m,
      paddingHorizontal: sidebarInset,
      borderRadius: radii.control,
      borderLeftWidth: 3,
      borderLeftColor: "transparent",
    },
    navItemActive: {
      backgroundColor: colors.navActiveSurface,
      borderLeftColor: colors.navActiveIndicator,
      ...shadows.sm,
    },
    navItemHover: { backgroundColor: "rgba(255, 255, 255, 0.6)" },
    navItemText: {
      color: colors.foreground,
      fontFamily: fonts.medium,
      fontSize: 14,
    },
    navItemTextActive: { color: colors.primary, fontFamily: fonts.medium },
    sidebarUtilityLink: {
      minHeight: 36,
      justifyContent: "center",
      paddingHorizontal: sidebarInset,
      paddingVertical: 6,
      borderRadius: radii.control,
      borderLeftWidth: 3,
      borderLeftColor: "transparent",
    },
    sidebarUtilityText: {
      color: colors.heading,
      fontFamily: fonts.regular,
      fontSize: 14,
    },
    sidebarUtilityTextActive: {
      color: colors.primary,
      fontFamily: fonts.medium,
    },
    utilityContent: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
    },
    planBadge: {
      minWidth: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 6,
      borderRadius: 9,
      backgroundColor: palette.brandMarigold,
    },
    planBadgeText: {
      color: palette.brandMidnight,
      fontFamily: fonts.bold,
      fontSize: 10,
    },
    sidebarFooter: {
      gap: spacing.s,
      paddingTop: spacing.l,
      marginTop: spacing.s,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    sidebarProfile: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.m,
      padding: spacing.xs,
      borderRadius: radii.control,
    },
    sidebarAvatar: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 18,
      backgroundColor: colors.primary,
      borderWidth: 2,
      borderColor: colors.elevated,
      ...shadows.sm,
    },
    sidebarAvatarText: {
      color: colors.primaryForeground,
      fontFamily: fonts.bold,
      fontSize: 12,
    },
    sidebarProfileName: {
      flex: 1,
      color: colors.heading,
      fontFamily: fonts.medium,
      fontSize: 13,
    },
    shellHeader: {
      minHeight: 58,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.xl,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.elevated,
      ...shadows.sm,
    },
    shellHeaderLeft: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    shellActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    headerIconButton: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.pill,
      backgroundColor: colors.elevated,
    },
    headerIconButtonHover: { backgroundColor: colors.backgroundSunken },
    breadcrumbs: {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "center",
    },
    breadcrumbItem: { flexDirection: "row", alignItems: "center" },
    breadcrumbCurrent: {
      flexShrink: 1,
      color: colors.heading,
      fontFamily: fonts.semibold,
      fontSize: 15,
    },
    breadcrumbSeparator: {
      color: colors.mutedForeground,
      fontFamily: fonts.regular,
      fontSize: 13,
      marginHorizontal: spacing.xs,
    },
    drawerOverlay: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.overlay,
    },
    drawerPanel: {
      width: "82%",
      maxWidth: 320,
      padding: spacing.l,
      backgroundColor: colors.surfaceNav,
    },
    drawerDismiss: { flex: 1 },
    modalOverlay: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
      backgroundColor: colors.overlay,
    },
    confirmationCard: {
      width: "100%",
      maxWidth: 420,
      padding: spacing.xl,
      borderRadius: radii.card,
      backgroundColor: colors.elevated,
      gap: spacing.l,
      ...shadows.raised,
    },
    modalTitle: {
      color: colors.heading,
      fontFamily: fonts.bold,
      fontSize: 22,
    },
    confirmationActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: spacing.s,
    },
  });
